import React, { useRef, useState } from 'react';
import { useMessages } from 'src/modules/messaging/hooks/api/messages';
import { useCreateWhatsAppMessage } from 'src/modules/messaging/whatsapp/hooks/api/messages-mutations';
import { useAlert } from 'src/contexts/alert-context';
import InfoError from 'src/components/atoms/info-error/info-error';
import { WhatsAppTemplateModal } from '../../../../whatsapp/components/organisms/templates/template-modal';
import { getContactAddress } from 'src/modules/messaging/utils/participant.util';
import { MessageItem } from '../../../molecules/messages/message-item';
import { MessageInput } from '../../../molecules/messages/message-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import type { Conversation, Participant } from 'src/api/generated';
import {
  ConversationChannel,
  SenderAvailabilityResponse,
  WhatsAppMessageType,
} from 'src/api/generated';
import {
  useInfiniteScroll,
  useScrollPosition,
  useScrollToBottom,
} from 'src/modules/shared/hooks/scroll/movement';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { isConversationExpired } from 'src/modules/messaging/utils/conversations/conversation.util';
import Loading from 'components/atoms/loading/loading';
import { SiWhatsapp } from 'react-icons/si';
import { useSenderAvailability } from 'src/modules/shared/hooks/api/messaging/sender-availability';
import { getWhatsAppSendErrorMessage } from 'src/modules/shared/utils/integrations/whatsapp-send-error.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationAddressPermission,
  IntegrationPermission,
} from 'src/modules/shared/types/permissions.type';
import { WhatsAppConnectionUnavailableGuidance } from 'src/modules/shared/messaging/components/molecules/whatsapp/connection-unavailable-guidance';

interface MessageListProps {
  conversation: Conversation;
  participants: Participant[];
}

export const MessageList: React.FC<MessageListProps> = ({
  conversation,
  participants,
}) => {
  const [isWhatsAppTemplateModalOpen, setIsWhatsAppTemplateModalOpen] =
    useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);

  const { showAlert } = useAlert();
  const { t } = useI18n();
  const { hasPermission: canOpenIntegrationsSettings } = useHasPermissions([
    IntegrationPermission.Read,
    IntegrationAddressPermission.Read,
  ]);

  const {
    messages,
    loading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMessages({
    conversationId: conversation.id,
  });

  const { isAtBottom, handleScroll } = useScrollPosition(messages);
  const { scrollToBottom } = useScrollToBottom({
    bottomAnchorRef,
    isAtBottom,
    dependencies: messages,
  });
  useInfiniteScroll({
    containerRef: scrollContainerRef,
    isFetching: isFetchingNextPage,
    hasMore: hasNextPage,
    fetchMore: fetchNextPage,
  });

  const { createWhatsAppMessage, loading: isSendingMessage } =
    useCreateWhatsAppMessage(
      // TODO: check when implementing i18n
      () => {
        showAlert('Mensaje enviado correctamente.', 'success');
      },
      (error) => {
        console.error('Error sending message:', error);
        showAlert(
          getWhatsAppSendErrorMessage(
            error,
            t('communications.messageBox.whatsapp.sendError'),
            t('communications.messageBox.whatsapp.connectionUnavailable')
          ),
          'error'
        );
      }
    );

  const fromPhoneNumber = conversation.mainParticipant.address;
  const fromPhoneNumberId = conversation.mainParticipant.providerId;
  const toPhoneNumber = getContactAddress(participants);
  const senderAvailabilityQuery = fromPhoneNumberId
    ? {
        channel: ConversationChannel.WHATSAPP,
        senderId: fromPhoneNumberId,
      }
    : {
        channel: ConversationChannel.WHATSAPP,
        sender: fromPhoneNumber,
      };
  const {
    data: availability,
    loading: availabilityLoading,
    error: availabilityError,
  } = useSenderAvailability(senderAvailabilityQuery);
  const connectionBlocked =
    availabilityLoading ||
    Boolean(availabilityError) ||
    availability?.state !== SenderAvailabilityResponse.state.AVAILABLE;
  const connectionUnavailable =
    connectionBlocked && !availabilityLoading && !availabilityError;

  const handleSendMessage = (message: string) => {
    if (!fromPhoneNumber || !toPhoneNumber || connectionBlocked) return;

    createWhatsAppMessage({
      content: message,
      fromPhoneNumber,
      toPhoneNumber,
      type: WhatsAppMessageType.TEXT,
    });
  };

  const conversationExpired = isConversationExpired(conversation);

  if (loading) return <Loading className="h-full items-center" />;
  if (error) return <InfoError error={error} />;

  return (
    <>
      <div
        className="flex-1 overflow-y-auto space-y-4 p-4 bg-gray-100 shadow-inner relative"
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <Loading className="h-full items-center" />
          </div>
        )}

        {messages
          .slice()
          .reverse()
          .map((message) => (
            <MessageItem key={message.id} message={message} />
          ))}

        <div ref={bottomAnchorRef} />

        {!isAtBottom && (
          <div className="sticky bottom-4 flex justify-end">
            <Button
              onClick={scrollToBottom}
              size="sm"
              variant="secondary"
              appearance="outline"
              className="rounded-full"
            >
              <ChevronDownIcon className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>

      {availabilityError && !availabilityLoading ? (
        <Alert
          variant="warning"
          size="sm"
          message={t('communications.messageBox.whatsapp.availabilityError')}
        />
      ) : connectionUnavailable ? (
        <Alert
          variant="warning"
          size="sm"
          message={
            <WhatsAppConnectionUnavailableGuidance
              canOpenIntegrationsSettings={canOpenIntegrationsSettings}
            />
          }
        />
      ) : null}

      <div className="flex-none border-t border-gray-200 p-4 items-end space-x-2 bg-white flex">
        <MessageInput
          onSendMessage={handleSendMessage}
          isSendingMessage={isSendingMessage}
          disabled={conversationExpired || connectionBlocked}
        />
        <Button
          onClick={() => setIsWhatsAppTemplateModalOpen(true)}
          size="sm"
          variant="secondary"
          appearance="outline"
          disabled={conversationExpired || connectionBlocked}
          className="flex items-center gap-1 px-2 py-1"
        >
          <SiWhatsapp className="h-4 w-4" />
          {/* TODO: check when implementing i18n */}
          Enviar plantilla
        </Button>
      </div>

      <WhatsAppTemplateModal
        isOpen={isWhatsAppTemplateModalOpen}
        onClose={() => setIsWhatsAppTemplateModalOpen(false)}
        fromPhoneNumberId={fromPhoneNumberId}
        initialPhoneAddress={fromPhoneNumber}
        toNumber={toPhoneNumber}
      />
    </>
  );
};
